import { CalendarClock, CheckCircle2, Clock3, RefreshCw, XCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useLiveResource } from "@/hooks/useLiveResource";
import { EmptyState, PageError, PageLoading, UnavailablePanel } from "@/components/LiveState";
import PageFrame from "@/components/agent-workspace/PageFrame";
import AutomationFocusList from "@/components/automations/AutomationFocusList";

const display = (value, fallback = "Not reported") => typeof value === "string" && value.trim() ? value : fallback;
const dateLabel = (value) => {
  if (!value) return "Not reported";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not reported" : date.toLocaleString();
};
const humanize = (value, fallback = "Not reported") => typeof value === "string" && value.trim()
  ? value.trim().replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
  : fallback;
const HEALTHY_DELIVERY_STATUSES = new Set(["complete", "completed", "delivered", "healthy", "sent", "success", "succeeded"]);
const FAILED_DELIVERY_STATUSES = new Set(["bounced", "error", "failed", "missed"]);
const deliveryTone = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  if (HEALTHY_DELIVERY_STATUSES.has(normalized)) return "healthy";
  if (FAILED_DELIVERY_STATUSES.has(normalized)) return "failed";
  return "reported";
};
const SLOT_LABELS = { sod: "Start of day", midday: "Midday", eod: "End of day" };
const slotLabel = (slot) => SLOT_LABELS[String(slot ?? "").trim().toLowerCase()] ?? humanize(slot, "Delivery");

function RetryButton({ testId, onRetry }) {
  return <button data-testid={testId} type="button" onClick={() => onRetry().catch(() => undefined)} className="btn-outline mt-3 inline-flex items-center gap-2"><RefreshCw size={13} /> Try again</button>;
}

function DailySource({ daily, onRetry }) {
  if (!daily?.available) return <div data-testid="daily-unavailable"><UnavailablePanel title="Daily experience source unavailable" description="Schedule and delivery evidence could not be read. No default schedule has been substituted." /><RetryButton testId="daily-retry" onRetry={onRetry} /></div>;
  const experience = daily.dailyExperience;
  if (!experience) return <EmptyState icon={CalendarClock} title="No daily experience record" description="The source is available but did not report a daily experience record." />;
  const schedule = [
    ["Start of day", experience.schedule?.startOfDay],
    ["Midday", experience.schedule?.midday],
    ["End of day", experience.schedule?.endOfDay],
  ];
  const health = Array.isArray(experience.recentDeliveryHealth) ? experience.recentDeliveryHealth : null;
  const deliveryReported = experience.deliveryHistoryReported === true;
  return (
    <section className="gos-card p-5" data-testid="daily-experience-source">
      <div className="flex items-center justify-between gap-3"><h2 className="font-display text-lg font-bold">Daily experience</h2><span className="text-xs text-sm-muted">{display(experience.timezone)}</span></div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {schedule.map(([label, value]) => <div key={label} className="rounded-xl border border-sm-ink/10 bg-sm-surface/50 p-4"><div className="text-xs text-sm-muted">{label}</div><div className="mt-1 font-mono text-sm font-semibold">{display(value, "Not scheduled")}</div></div>)}
      </div>
      <div className="mt-5 border-t border-sm-ink/5 pt-5" data-testid="daily-delivery-evidence">
        <h3 className="text-sm font-semibold text-sm-ink">Recent delivery evidence</h3>
        {!deliveryReported || !health ? (
          <p className="mt-2 text-xs text-sm-muted">Delivery history was not reported by this source.</p>
        ) : health.length === 0 ? (
          <p className="mt-2 text-xs text-sm-muted">Delivery history was reported, with no recent records.</p>
        ) : (
          <div className="mt-3 divide-y divide-sm-ink/5 rounded-xl border border-sm-ink/10">
            {health.map((record, index) => {
              const tone = deliveryTone(record.status);
              const Icon = tone === "healthy" ? CheckCircle2 : tone === "failed" ? XCircle : Clock3;
              return (
                <div key={`${record.slot || "delivery"}-${record.localDate || index}-${index}`} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <Icon size={16} aria-hidden="true" className={`mt-0.5 shrink-0 ${tone === "healthy" ? "text-[#16A34A]" : tone === "failed" ? "text-[#EF4343]" : "text-sm-primary"}`} />
                    <div className="min-w-0"><div className="text-sm font-semibold text-sm-ink">{slotLabel(record.slot)} · {display(record.localDate)}</div><div className="mt-1 truncate text-xs text-sm-muted">{display(record.targetLabel, "Target not reported")}</div></div>
                  </div>
                  <div className="text-left text-xs sm:text-right">
                    <div className={`font-semibold ${tone === "healthy" ? "text-[#16A34A]" : tone === "failed" ? "text-[#EF4343]" : "text-sm-primary"}`}>{humanize(record.status)}</div>
                    <div className="mt-1 text-sm-muted">{record.receiptAvailable === true ? "Receipt available" : record.receiptAvailable === false ? "No receipt reported" : "Receipt state not reported"}</div>
                    <div className="mt-1 text-sm-muted">Updated {dateLabel(record.updatedAt)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function AutomationsBody({ embedded = false }) {
  const { activeClient } = useApp();
  const resource = useLiveResource("getAgentAutomations");
  if (resource.status === "loading" && !resource.data) return <PageLoading label="Loading published automations…" />;
  if (resource.status === "error" || resource.error) return <PageError error={resource.error} onRetry={resource.reload} title="Automation sources unavailable" />;
  const jobs = resource.data?.jobs;

  const refresh = <button className="btn-outline inline-flex items-center gap-2" type="button" onClick={() => resource.reload().catch(() => undefined)} disabled={resource.status === "loading"}><RefreshCw size={13} />Refresh</button>;
  const content = (
      <div className="space-y-6">
        <section data-testid="published-jobs">
          <div className="mb-3 flex items-center gap-2"><Clock3 size={16} className="text-sm-primary" /><h2 className="font-display text-lg font-bold">Published automations</h2></div>
          {!jobs?.available ? <div data-testid="published-jobs-unavailable"><UnavailablePanel title="Published automations unavailable" description="The published job inventory could not be read. This does not mean there are no jobs." /><RetryButton testId="automations-retry" onRetry={resource.reload} /></div> : jobs.items.length === 0 ? <EmptyState icon={Clock3} title="No published automations" description="The published inventory is available and empty." /> : (
            <AutomationFocusList jobs={jobs.items} activeClient={activeClient} />
          )}
        </section>
        <DailySource daily={resource.data?.daily} onRetry={resource.reload} />
      </div>
  );
  return embedded ? <div data-testid="agent-automations-page"><div className="sa-refresh">{refresh}</div>{content}</div> : <PageFrame title="Automations" subtitle="Every job, every schedule, one clear view." actions={refresh} testId="agent-automations-page">{content}</PageFrame>;
}

export default function AgentAutomations({ embedded = false }) {
  const { activeClient } = useApp();
  return <AutomationsBody key={activeClient?.clientSlug ?? ""} embedded={embedded} />;
}
